import styled from "@emotion/styled";
import { BreadcrumbTitle } from "@shared/components/PageBreadcrumbs";
import { VideoCard, VideoCardSkeleton } from "./VideoCard";
import { useMemo, useState } from "react";
import { learningCenterData } from "./learning-center-data";
import { useLearningCenterStore } from "./learning-center.store";
import { useGetLearningCenterLoomVideos } from "./useGetLearningCenterLoomVideos";
import { OpenedVideoDialog } from "./OpenedVideoDialog";
import { Typography } from "@mui/material";
import { appEventEmitter } from "@shared/events/app-event-emitter";

const Container = styled.div`
  padding-bottom: 8rem;
`;

const SectionContainer = styled.div`
  margin-bottom: 3rem;
`;

const SectionVideosContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;
  position: relative;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }

  &:not(:last-of-type) {
    margin-bottom: 3rem;
  }
`;

export function LearningCenter() {
  const [openedVideoId, setOpenedVideoId] = useState<string | null>(null);
  const videos = useLearningCenterStore((state) => state.videos);
  const videosMap = useLearningCenterStore((state) => state.videosMap);
  const setVideos = useLearningCenterStore((state) => state.setVideos);

  const query = useGetLearningCenterLoomVideos({
    enabled: true,
    onSuccess: (data) => {
      setVideos(data);
    },
  });

  const openedVideo = useMemo(
    () => videosMap.get(openedVideoId ?? "") ?? null,
    [openedVideoId, videosMap]
  );

  const onClickCard = (params: { videoId: string; videoTitle: string }) => {
    const { videoId, videoTitle } = params;
    setOpenedVideoId(videoId);
    appEventEmitter.emit({
      eventType: "sidekick-learning-video-click",
      videoId,
      videoTitle,
    });
  };

  return (
    <Container>
      {learningCenterData.sections.map((section) => (
        <SectionContainer key={section.title} id={section.id}>
          <BreadcrumbTitle mb={2}>{section.title}</BreadcrumbTitle>
          <SectionVideosContainer>
            {query.isLoading && (
              <>
                <VideoCardSkeleton />
                <VideoCardSkeleton />
                <VideoCardSkeleton />
              </>
            )}
            {!query.isLoading &&
              videos.length > 0 &&
              section.videos.map((v) => {
                const video = videosMap.get(v.id);
                if (!video) return null;
                return (
                  <VideoCard
                    key={video.id}
                    videoId={video.id}
                    videoTitle={video.title}
                    videoThumbnailUrl={video.thumbnailUrl}
                    videoDuration={video.duration}
                    onClickCard={onClickCard}
                  />
                );
              })}
            {!query.isLoading && videos.length === 0 && (
              <Typography variant="body2">No videos found</Typography>
            )}
          </SectionVideosContainer>
        </SectionContainer>
      ))}
      <OpenedVideoDialog
        video={openedVideo}
        onClose={() => setOpenedVideoId(null)}
      />
    </Container>
  );
}
